import React, { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { Box, Button } from "@mui/material";
import * as Sentry from "@sentry/react";
import defer from "lodash/defer";
import { enqueueSnackbar } from "notistack";

import { LoadingButton } from "ui";

import { selectCurrentAccount, selectFoldersForAddingProject, selectProjectsByParentId, useCreateProjectMutation } from "fond/api";
import { HOME_FOLDER_ID } from "fond/constants";
import { TextField } from "fond/form/fields";
import { FolderPickerField } from "fond/form/fields/FolderPickerField";
import { TextFieldValue } from "fond/form/fields/TextField";
import { useEntityUtil } from "fond/hooks/useEntityUtil";
import { Folder, HomeFolder, Maybe, Project, ProjectPreview, ProjectSubtype, ProjectType } from "fond/types";
import { setValue } from "fond/utils/formMutators";
import { useAppSelector } from "fond/utils/hooks";
import { generateUnusedName } from "fond/utils/naming";
import { required } from "fond/utils/validation";
import { Message, Modal } from "fond/widgets";

type CreateProjectFormData = {
  Name: string;
  FolderId?: string;
};

export type NewProjectDialogProps = {
  projectType: ProjectType;
  projectSubtype?: ProjectSubtype;
  /**
   * The parent folder where this item will be created
   */
  parentId?: string | null;
  /**
   * Callback function to handle the onClose of the modal
   */
  onClose(): void;
  /**
   * Callback function to signal when the project creation has completed
   */
  onComplete(project: Project): void;
};

export const NewProjectDialog: React.FC<NewProjectDialogProps> = ({
  projectType,
  projectSubtype,
  parentId = null,
  onClose,
  onComplete,
}: NewProjectDialogProps) => {
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(parentId ?? HOME_FOLDER_ID);
  const siblingEntities = useAppSelector((state) =>
    selectProjectsByParentId(state, selectedFolderId === HOME_FOLDER_ID || !selectedFolderId ? undefined : selectedFolderId)
  );
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [createProject, { isLoading: isSaving, isError, error }] = useCreateProjectMutation();
  const folders = useAppSelector((state) => selectFoldersForAddingProject(state, currentAccount));
  const { getEntityLabel } = useEntityUtil();

  const onInputRefReceived = (el: HTMLInputElement) => {
    if (el) {
      // defer selection to give the input time to render
      // and the selection to take place in the next cycle
      defer(() => el.select());
    }
  };

  // Suggest a default name that doesn't overlap with any existing one.
  const defaultNameValue = useMemo(
    () =>
      generateUnusedName(
        `Untitled ${getEntityLabel({ EntityType: "project", Type: projectType, Subtype: projectSubtype }, "short").toLowerCase()} project`,
        siblingEntities.map((e) => e.ProjectName)
      ),
    [siblingEntities, projectType, projectSubtype, getEntityLabel]
  );

  /**
   * On submit function called when the form is submitted and valid
   */
  const handleOnSubmit = async ({ Name, FolderId }: CreateProjectFormData) => {
    if (!currentAccount) {
      enqueueSnackbar("There was an issue creating this project. Could not get account context.", { variant: "error" });
      return;
    }

    try {
      const project = await createProject({
        folderId: FolderId === HOME_FOLDER_ID ? null : FolderId,
        accountId: currentAccount.ID,
        type: projectType,
        subType: projectSubtype,
        name: Name,
      }).unwrap();
      onComplete(project);
    } catch (e) {
      enqueueSnackbar("There was an issue creating this project. Please try again.", { variant: "error" });
      Sentry.captureException(e);
    }
  };

  const handleChangeFolder = (event: React.SyntheticEvent, option: Maybe<Folder | HomeFolder>) => {
    setSelectedFolderId(option?.ID);
  };

  /**
   * Validates that the Name is currently not in use
   * @param value New Name value
   */
  const uniqueName = (value: TextFieldValue) => {
    const newName = value?.toString().toLowerCase().trim();
    const existingProject = siblingEntities.some((entity: ProjectPreview) => entity.ProjectName.toLowerCase().trim() === newName);
    if (existingProject) {
      return "This name is already in use. Please use another.";
    }
    return undefined;
  };

  const formValidator = (values: CreateProjectFormData) => {
    return {
      Name: required(values.Name) || uniqueName(values.Name),
    };
  };

  let errorMessage = "There was an issue creating the project. Please try again.";
  if (error && "status" in error && !("error" in error)) {
    if (error.status === 422) {
      errorMessage = "Could not create project in this folder.";
    }
    if (error.status === 403) {
      errorMessage = "Not permitted to create a project of this type.";
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      data-testid="new-project-modal"
      DialogTitleProps={{ sx: { mb: 0.5 } }}
      sx={{ ".subtitle": { fontSize: (theme) => theme.typography.size.normal } }}
      header="Create a new project"
      headerSubtitle={getEntityLabel({ EntityType: "project", Type: projectType, Subtype: projectSubtype }, "short")}
      variant="secondary"
      content={
        <Box>
          <Box display="flex" flexDirection="row" justifyContent="space-between" gap={4}>
            <Box flex={1}>
              <Form<CreateProjectFormData>
                initialValues={{
                  Name: defaultNameValue,
                  FolderId: parentId || HOME_FOLDER_ID,
                }}
                keepDirtyOnReinitialize
                onSubmit={handleOnSubmit}
                validate={formValidator}
                render={({ handleSubmit }) => {
                  return (
                    <form onSubmit={handleSubmit} id="new-project-form">
                      <Box maxWidth={380}>
                        <TextField
                          name="Name"
                          required
                          label="Project name"
                          autoFocus
                          autoComplete="off"
                          inputProps={{ ref: onInputRefReceived }}
                          fullWidth
                        />
                        <FolderPickerField name="FolderId" folders={folders} account={currentAccount!} compact onChange={handleChangeFolder} />
                      </Box>
                    </form>
                  );
                }}
                mutators={{ setValue }}
              />
              <Box maxWidth={380} display="flex" alignItems="center" flexDirection="column" gap={1}>
                {isError && (
                  <Box mt={2}>
                    <Message type="error">{errorMessage}</Message>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      }
      actions={
        <>
          <Button data-testid="new-project-cancel-button" color="primary" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton data-testid="new-project-save-button" color="primary" loading={isSaving} type="submit" form="new-project-form">
            Create
          </LoadingButton>
        </>
      }
    />
  );
};
